import { Component, OnInit } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { GetDomainPipe } from '@app/modules/direct/pipes/get-domain.pipe';
import { DialogDeleteGenericComponent } from '@app/shared/components/dialog-delete-generic/dialog-delete-generic.component';
import { NotificationType } from '@app/shared/interfaces';
import { PropertiesService } from '@app/shared/services/properties/properties.service';
import { ToastNotificationsService } from '@app/shared/services/toast-notifications/toast-notifications.service';
import { forkJoin, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { CustomSite, CustomSitesService } from '../../services/custom-sites.service';
import { DirectService, FullTemplate } from '../../services/direct.service';

interface NavItem {
  id: string;
  label: string;
  icon: string;
}

@Component({
  standalone: false,
  selector: 'sbnb-direct-custom-site-edit',
  templateUrl: './direct-custom-site-edit.component.html',
  styleUrls: ['./direct-custom-site-edit.component.css'],
  providers: [GetDomainPipe],
})
export class DirectCustomSiteEditComponent implements OnInit {
  navItems: NavItem[] = [
    {
      id: 'properties',
      label: 'Properties',
      icon: 'home',
    },
    {
      id: 'styling',
      label: 'Styling',
      icon: 'brush',
    },
    {
      id: 'search-widget',
      label: 'Property Search',
      icon: 'search',
    },
  ];

  activeNavItem: NavItem = this.navItems[0];
  properties$ = of([]);

  site: CustomSite;
  saving: boolean = false;

  deleting: boolean = false;

  previewPropertyId: number = null;
  widgetPreviewSrcUrl: SafeResourceUrl;

  templates: FullTemplate[] = [];

  constructor(
    private readonly dialog: MatDialog,
    private propertiesService: PropertiesService,
    private customSiteService: CustomSitesService,
    private route: ActivatedRoute,
    private direct: DirectService,
    private toast: ToastNotificationsService,
    private router: Router,
    private sanitizer: DomSanitizer,
    private getDomainPipe: GetDomainPipe
  ) {}

  ngOnInit() {
    this.getTemplates();

    this.route.params.subscribe((params) => {
      this.fetchCustomSite(params.uuid);
    });
  }

  fetchCustomSite(uuid: string) {
    this.customSiteService.getCustomSite(uuid).subscribe((res) => {
      this.site = res;

      this.properties$ = this.propertiesService.getDirectProperties(null, this.site.uuid, null, true).pipe(
        tap((properties) => {
          this.previewPropertyId = properties[0].id;
          this.refreshPreview();
        })
      );
    });
  }

  propertiesChange(selectedProperties: number[]) {
    this.site.property_ids = selectedProperties;
  }

  save() {
    this.saving = true;

    const updateCustomSite$ = this.customSiteService.updateCustomSite(this.site.uuid, this.site);
    const saveSiteProperties$ = this.direct.saveSiteProperties(this.site.uuid, 'custom', this.site.property_ids, false);

    forkJoin([updateCustomSite$, saveSiteProperties$]).subscribe(
      (results) => {
        this.saving = false;
      },
      (err) => {
        this.saving = false;
      }
    );
  }

  refreshPreview() {
    this.widgetPreviewSrcUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
      `https://booking.hospitable.com/widget/${this.site.booking_widget_uuid}/${this.previewPropertyId}?theme=${
        this.site.theme
      }&styles=${encodeURIComponent(JSON.stringify(this.site.styles))}&preview=true`
    );
  }

  getTemplates() {
    this.direct.getSiteTemplates().subscribe((res) => {
      this.templates = res;
    });
  }

  themeUpdated() {
    this.site.styles = {
      primary_color: null,
      secondary_color: null,
      tertiary_color: null,
    };

    this.refreshPreview();
  }

  deleteCustomSitePrompt() {
    const dialogRef = this.dialog.open(DialogDeleteGenericComponent, {
      width: '720px',
      data: {
        name: this.getDomainPipe.transform(this.site.properties[0]?.url),
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.deleteCustomSite();
      }
    });
  }

  deleteCustomSite() {
    this.deleting = true;
    this.customSiteService.deleteCustomSite(this.site.uuid).subscribe(
      (res) => {
        this.deleting = false;
        this.toast.open(`Site deleted`, 'Dismiss', NotificationType.Success);
        this.router.navigate(['/direct']);
      },
      (err) => {
        this.deleting = false;
        this.toast.open('Failed to delete site', 'Dismiss', NotificationType.Error);
      }
    );
  }
}
